import { useQuery } from "@tanstack/react-query";
import { createFileRoute, Link } from "@tanstack/react-router";
import { useState } from "react";
import { Banner, Button, Card, Field, Input, Select } from "@magarac/ui";
import { LOCAL_PORTS } from "@magarac/shared/config";
import { jobTypeListSchema } from "@magarac/shared/schemas/jobType";
import type { SettingsBooking } from "@magarac/shared/schemas/settings";
import { HelpIcon } from "../../../components/HelpIcon";
import { api } from "../../../lib/api";
import { messageFor } from "../../../lib/errors";
import { useSetting } from "../../../lib/settings";
import { useTenant } from "../../../lib/tenant";

export const Route = createFileRoute("/_app/settings/booking")({
  component: BookingSettingsPage,
});

const DEFAULTS: SettingsBooking = { enabled: false, leadHours: 2, sameDay: true, maxDaysAhead: 30 };

/** Where the booking page lives, next to this app (app. -> book.). */
function bookingBase(): string {
  const { protocol, hostname } = window.location;
  if (hostname === "localhost" || hostname === "127.0.0.1")
    return `http://localhost:${LOCAL_PORTS.booking}`;
  return `${protocol}//${hostname.replace(/^(staging-)?app\./, "$1book.")}`;
}

/** SCH-19, SCH-22: turn on the public booking page and share its link. Screen key: settings.booking */
function BookingSettingsPage() {
  const { active } = useTenant();
  const { query, save, value } = useSetting("booking");
  const services = useQuery({
    queryKey: ["job-types", active?.id, "active"],
    queryFn: () => api.get("/job-types", jobTypeListSchema),
    enabled: Boolean(active),
  });
  const bookable = (services.data?.jobTypes ?? []).filter((j) => j.active && j.bookableOnline);
  const link = active ? `${bookingBase()}/${active.slug}` : "";

  return (
    <div>
      <div className="mb-6 flex items-center justify-between">
        <h1 className="text-2xl font-semibold">Online booking</h1>
        <HelpIcon screen="settings.booking" />
      </div>
      {query.isPending ? (
        <p className="text-muted">Loading…</p>
      ) : (
        <BookingForm
          key={active?.id ?? "none"}
          initial={value ?? DEFAULTS}
          link={link}
          bookableCount={services.data ? bookable.length : null}
          onSave={(v) => save.mutateAsync(v)}
          busy={save.isPending}
          error={save.error ? messageFor(save.error) : null}
          saved={save.isSuccess}
        />
      )}
    </div>
  );
}

function BookingForm({
  initial,
  link,
  bookableCount,
  onSave,
  busy,
  error,
  saved,
}: {
  initial: SettingsBooking;
  link: string;
  bookableCount: number | null;
  onSave: (v: SettingsBooking) => Promise<unknown>;
  busy: boolean;
  error: string | null;
  saved: boolean;
}) {
  const [form, setForm] = useState<SettingsBooking>(initial);
  const [copied, setCopied] = useState(false);
  const embed = `<iframe src="${link}" style="width:100%;min-height:720px;border:0" title="Book online"></iframe>`;
  const copy = async (text: string) => {
    try {
      await navigator.clipboard.writeText(text);
      setCopied(true);
    } catch {
      setCopied(false);
    }
  };

  return (
    <>
      <Card title="Your booking page" className="mb-6">
        {error ? <Banner tone="danger">{error}</Banner> : null}
        {saved ? <Banner tone="ok">Saved.</Banner> : null}
        {bookableCount === 0 ? (
          <Banner tone="warn">
            No services can be booked online yet. Turn on “Customers can book this online” for at
            least one service on the <Link to="/services">Services</Link> screen.
          </Banner>
        ) : null}
        <label className="mb-4 flex items-center gap-3">
          <input
            type="checkbox"
            className="h-5 w-5"
            checked={form.enabled}
            onChange={(e) => setForm({ ...form, enabled: e.target.checked })}
          />
          <span className="font-medium">Customers can book online</span>
        </label>
        <Field
          id="booking-lead"
          label="Earliest booking"
          hint="How much notice you need before a visit booked online."
        >
          <Select
            id="booking-lead"
            value={String(form.leadHours)}
            onChange={(e) => setForm({ ...form, leadHours: Number(e.target.value) })}
          >
            {[0, 1, 2, 4, 12, 24, 48, 72].map((h) => (
              <option key={h} value={h}>
                {h === 0
                  ? "Any open time"
                  : h < 24
                    ? `${h} hour${h === 1 ? "" : "s"} ahead`
                    : `${h / 24} day${h === 24 ? "" : "s"} ahead`}
              </option>
            ))}
          </Select>
        </Field>
        <label className="mb-4 flex items-center gap-3">
          <input
            type="checkbox"
            className="h-5 w-5"
            checked={form.sameDay}
            onChange={(e) => setForm({ ...form, sameDay: e.target.checked })}
          />
          <span>Allow bookings for today</span>
        </label>
        <Field id="booking-horizon" label="How far ahead customers can book (days)">
          <Input
            id="booking-horizon"
            type="number"
            min={1}
            max={120}
            value={form.maxDaysAhead}
            onChange={(e) =>
              setForm({
                ...form,
                maxDaysAhead: Math.min(120, Math.max(1, Math.round(Number(e.target.value) || 1))),
              })
            }
          />
        </Field>
        <Button busy={busy} onClick={() => void onSave(form)}>
          Save
        </Button>
      </Card>

      <Card title="Share it">
        <p className="mb-2 text-sm text-muted">
          Put this link on your website, Google Business Profile, and social pages.
        </p>
        <p className="mb-3 break-all font-mono text-sm">{link}</p>
        <div className="mb-4 flex flex-wrap gap-2">
          <Button variant="secondary" onClick={() => void copy(link)}>
            Copy link
          </Button>
          <a
            href={link}
            target="_blank"
            rel="noreferrer"
            className="inline-flex min-h-11 items-center rounded-lg px-4 text-brand-700 underline"
          >
            Open the page
          </a>
        </div>
        <p className="mb-2 text-sm text-muted">
          Or show it inside your own website: paste this where the booking form should appear.
        </p>
        <pre className="mb-3 overflow-x-auto rounded-lg bg-canvas p-3 text-xs">{embed}</pre>
        <Button variant="secondary" onClick={() => void copy(embed)}>
          Copy website code
        </Button>
        {copied ? <p className="mt-2 text-sm text-muted">Copied.</p> : null}
        {!initial.enabled ? (
          <p className="mt-3 text-sm text-muted">
            Until you turn online booking on and save, the page tells visitors to call you.
          </p>
        ) : null}
      </Card>
    </>
  );
}
